import * as React from 'react'
import { Plus } from 'lucide-react'
import type { IrrigationEquipmentDto, ZoneDto } from '@lib/dto/irrigation.dto'
import { equipmentBoxId } from '@lib/dto/irrigation.dto'
import { PIPE_SIZE_LABELS } from '@lib/enums/irrigation.enum'
import { MAX_ZONE_NUMBER } from '@lib/input/irrigation.input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Labeled,
  LinkRow,
  Section,
  SelectField,
} from '@/irrigation-survey/fields'
import {
  newEquipment,
  newZone,
  pinOfRecord,
  withBox,
} from '@/irrigation-survey/irrigationDrafts'
import type { IrrigationChange } from '@/irrigation-survey/irrigationOperations'
import {
  boxContents,
  equipmentName,
  timerName,
  zoneName,
  zonesInOrder,
} from '@/irrigation-survey/irrigationLabels'
import type { EditorContext } from '@/irrigation-survey/IrrigationEditors'

type ValveBox = Extract<IrrigationEquipmentDto, { kind: 'VALVE_BOX' }>
type Valve = Extract<IrrigationEquipmentDto, { kind: 'VALVE' }>

/** Where a zone's valve is, from one box's point of view. */
type ZoneValve =
  | { state: 'here'; valve: Valve }
  | { state: 'elsewhere'; valve: Valve }
  | { state: 'none' }

function isValve(item: IrrigationEquipmentDto): item is Valve {
  return item.kind === 'VALVE'
}

function zoneValve(
  zone: ZoneDto,
  boxId: string,
  equipment: readonly IrrigationEquipmentDto[],
): ZoneValve {
  const valve = equipment
    .filter(isValve)
    .find((item) => item.zoneId === zone.id)
  if (valve === undefined) return { state: 'none' }
  return valve.boxId === boxId
    ? { state: 'here', valve }
    : { state: 'elsewhere', valve }
}

/** What else is commonly found in a box, besides zone valves. */
const OTHER_CONTENTS = [
  { kind: 'MASTER_VALVE', label: 'Master valve' },
  { kind: 'BALL_VALVE', label: 'Ball valve' },
  { kind: 'SENSOR', label: 'Flow sensor' },
  { kind: 'OTHER', label: 'Something else' },
] as const

const CHIP =
  'h-11 min-w-11 rounded-full border px-3 text-sm tabular-nums transition-colors lg:h-8'

const CHIP_STATE: Record<ZoneValve['state'], string> = {
  here: 'border-brand bg-brand text-primary-foreground',
  elsewhere: 'border-dashed border-strong bg-surface text-muted-text',
  none: 'border-default bg-surface text-secondary-text hover:bg-subtle',
}

/**
 * The timer whose zones the box shows first: the one of the zone being
 * walked, else of a valve already in the box, else the site's first.
 */
function firstTimerId(box: ValveBox, context: EditorContext): string | null {
  const { site } = context
  const walking = site.zones.find((zone) => zone.id === context.currentZoneId)
  if (walking !== undefined) return walking.timerId
  const inside = boxContents(box.id, site)
    .filter(isValve)
    .map((valve) => site.zones.find((zone) => zone.id === valve.zoneId))
    .find((zone) => zone !== undefined)
  return inside?.timerId ?? site.timers[0]?.id ?? null
}

/** A line under a record in the box: its part, or a valve's size. */
function contentsDetail(item: IrrigationEquipmentDto): string | undefined {
  const size = 'size' in item && item.size !== null ? item.size : null
  const parts = [
    item.part?.name ?? null,
    size === null ? null : PIPE_SIZE_LABELS[size],
  ].filter((part): part is string => part !== null)
  return parts.length === 0 ? undefined : parts.join(' · ')
}

/**
 * What is in a valve box, said on the box's sheet: the zones whose valves are
 * in it, tapped as chips ("2, 3, 4 and 5"), and anything else with them.
 *
 * Each tap is saved at once, as a record of its own, the way dropping a valve
 * is: the box's own fields wait for Save, its contents do not. A zone whose
 * valve is recorded somewhere else (pinned on its own, or in another box)
 * asks before moving it, since that valve has a history of its own. A chip
 * tapped by mistake can be tapped again to take the valve back, until the
 * sheet closes; after that a tap opens the valve's sheet.
 *
 * A box's first contents are its record being filled in, as a timer's first
 * zones are. After that a revisit's additions are sent unanswered, which the
 * server records as new (docs/site-visits.md).
 */
export function ValveBoxContents({
  box,
  context,
}: {
  box: ValveBox
  context: EditorContext
}) {
  const { site } = context
  const contents = boxContents(box.id, site)
  const [chosenTimerId, setChosenTimerId] = React.useState(() =>
    firstTimerId(box, context),
  )
  const [addedHere, setAddedHere] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const [movingZoneId, setMovingZoneId] = React.useState<string | null>(null)
  const [typed, setTyped] = React.useState('')
  const [message, setMessage] = React.useState<string | null>(null)
  const locked = context.canEditRecord === false
  const timer =
    site.timers.find((item) => item.id === chosenTimerId) ?? site.timers[0]
  const timerZones =
    timer === undefined
      ? []
      : zonesInOrder(site).filter((zone) => zone.timerId === timer.id)
  const answer = contents.length === 0 ? true : undefined
  const movingZone = site.zones.find((zone) => zone.id === movingZoneId)
  const moving =
    movingZone === undefined
      ? null
      : zoneValve(movingZone, box.id, site.equipment)

  function commit(change: IrrigationChange, isCorrection?: boolean): boolean {
    const result = context.commit(change, isCorrection)
    if (!result.ok) {
      setMessage(result.message)
      return false
    }
    setMessage(null)
    return true
  }

  function remember(id: string): void {
    setAddedHere((current) => new Set([...current, id]))
  }

  function addValve(zoneId: string | null, isCorrection = answer): void {
    const valve = newEquipment(site, 'VALVE', pinOfRecord(box), {
      currentZoneId: zoneId,
      boxId: box.id,
    })
    if (
      commit(
        { kind: 'upsert', entity: 'equipment', record: valve },
        isCorrection,
      )
    ) {
      remember(valve.id)
    }
  }

  function tapZone(zone: ZoneDto): void {
    setMovingZoneId(null)
    const where = zoneValve(zone, box.id, site.equipment)
    switch (where.state) {
      case 'none':
        addValve(zone.id)
        return
      case 'elsewhere':
        setMovingZoneId(zone.id)
        return
      case 'here':
        if (addedHere.has(where.valve.id)) {
          // Tapped by mistake a moment ago: take it back as recorded by mistake.
          if (
            commit(
              { kind: 'archive', entity: 'equipment', id: where.valve.id },
              true,
            )
          ) {
            setAddedHere(
              (current) =>
                new Set([...current].filter((id) => id !== where.valve.id)),
            )
          }
          return
        }
        context.open({ entity: 'equipment', record: where.valve, isNew: false })
    }
  }

  function moveHere(valve: Valve): void {
    if (
      commit(
        {
          kind: 'upsert',
          entity: 'equipment',
          record: withBox(valve, box.id),
        },
        answer,
      )
    ) {
      setMovingZoneId(null)
    }
  }

  /** A zone number typed in: one the timer has is tapped, one it lacks added. */
  function addNumber(): void {
    if (timer === undefined) return
    const number = Number(typed)
    if (!Number.isInteger(number) || number < 1 || number > MAX_ZONE_NUMBER) {
      setMessage(`Zone numbers run from 1 to ${String(MAX_ZONE_NUMBER)}.`)
      return
    }
    setTyped('')
    const existing = timerZones.find((zone) => zone.number === number)
    if (existing !== undefined) {
      tapZone(existing)
      return
    }
    const isCorrection = answer
    const zone = newZone(site, timer.id, number)
    if (
      commit({ kind: 'upsert', entity: 'zone', record: zone }, isCorrection)
    ) {
      addValve(zone.id, isCorrection)
    }
  }

  function addOther(kind: (typeof OTHER_CONTENTS)[number]['kind']): void {
    const made = newEquipment(site, kind, pinOfRecord(box), {
      currentZoneId: null,
      boxId: box.id,
    })
    // "Something else" needs a name before it can be saved.
    if (made.kind === 'OTHER') {
      context.open({ entity: 'equipment', record: made, isNew: true })
      return
    }
    const record: IrrigationEquipmentDto =
      made.kind === 'SENSOR' ? { ...made, sensorType: 'FLOW' } : made
    if (commit({ kind: 'upsert', entity: 'equipment', record }, answer)) {
      context.open({ entity: 'equipment', record, isNew: false })
    }
  }

  function renderZones(): React.ReactNode {
    if (timer === undefined) {
      return (
        <p className="text-sm text-muted-text">
          No timers yet, so no zones to choose. A valve whose zone is not known
          yet can still go in.
        </p>
      )
    }
    return (
      <>
        {site.timers.length > 1 ? (
          <SelectField
            label="Timer"
            id={`box-timer-${box.id}`}
            value={timer.id}
            options={site.timers.map((item) => ({
              value: item.id,
              label: timerName(item, site.timers),
            }))}
            onChange={(id) => {
              setChosenTimerId(id)
              setMovingZoneId(null)
            }}
          />
        ) : null}
        <fieldset className="space-y-1">
          <legend className="text-xs font-medium text-secondary-text">
            Zones whose valves are in it
          </legend>
          {timerZones.length === 0 ? (
            <p className="text-sm text-muted-text">
              This timer has no zones yet. Add one by its number below.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {timerZones.map((zone) => {
                const where = zoneValve(zone, box.id, site.equipment)
                return (
                  <button
                    key={zone.id}
                    type="button"
                    className={cn(CHIP, CHIP_STATE[where.state])}
                    aria-pressed={where.state === 'here'}
                    aria-label={
                      where.state === 'elsewhere'
                        ? `Zone ${String(zone.number)}, valve recorded elsewhere`
                        : `Zone ${String(zone.number)}`
                    }
                    onClick={() => {
                      tapZone(zone)
                    }}
                  >
                    {zone.number}
                  </button>
                )
              })}
            </div>
          )}
          <p className="text-xs text-muted-text">
            Tap each zone whose valve is in this box. A dashed zone has its
            valve recorded somewhere else.
          </p>
        </fieldset>
        {movingZone === undefined || moving?.state !== 'elsewhere' ? null : (
          <div className="space-y-2 rounded-md border border-default px-3 py-2">
            <p className="text-sm">
              {zoneName(movingZone, site.timers)}’s valve is recorded{' '}
              {equipmentBoxId(moving.valve) === null
                ? 'at a pin of its own'
                : 'in another valve box'}
              . Move it into this box?
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  moveHere(moving.valve)
                }}
              >
                Move it here
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setMovingZoneId(null)
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        <Labeled
          label="A zone not listed"
          id={`box-zone-${box.id}`}
          hint={`Adds it to ${timerName(timer, site.timers)}, with its valve in this box.`}
        >
          <div className="flex gap-2">
            <input
              id={`box-zone-${box.id}`}
              inputMode="numeric"
              className="h-11 w-24 rounded-md border border-default bg-surface px-2.5 text-sm text-primary-text transition-colors hover:border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 lg:h-9"
              placeholder="e.g. 23"
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value)
                setMessage(null)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') addNumber()
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="h-11 lg:h-9"
              disabled={typed.trim() === ''}
              onClick={addNumber}
            >
              Add zone
            </Button>
          </div>
        </Labeled>
      </>
    )
  }

  return (
    <Section title="In this box">
      <div className="space-y-3">
        {locked ? null : renderZones()}
        {contents.length === 0 ? (
          <p className="text-sm text-muted-text">Nothing recorded in it yet.</p>
        ) : (
          <ul className="divide-y divide-default rounded-md border border-default">
            {contents.map((item) => (
              <LinkRow
                key={item.id}
                title={equipmentName(item, site)}
                detail={contentsDetail(item)}
                pending={context.pendingKeys.has(`equipment:${item.id}`)}
                onClick={() => {
                  context.open({
                    entity: 'equipment',
                    record: item,
                    isNew: false,
                  })
                }}
              />
            ))}
          </ul>
        )}
        {locked ? null : (
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                addValve(null)
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Valve, zone not known
            </Button>
            {OTHER_CONTENTS.map((entry) => (
              <Button
                key={entry.kind}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  addOther(entry.kind)
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {entry.label}
              </Button>
            ))}
          </div>
        )}
        {message === null ? null : (
          <p role="alert" className="text-sm text-danger">
            {message}
          </p>
        )}
      </div>
    </Section>
  )
}
